/**
 * Persisting a drag-to-reorder.
 *
 * PostgREST cannot set a different value per row in one PATCH, and issuing N
 * PATCHes makes the order visibly wrong in between. An upsert of `{id,
 * sort_order}` pairs is one statement and one transaction, so the app never
 * reads a half-applied order (US-A02 AC4, US-A03 AC4, US-A11 AC5).
 */
import type { Database } from '@horizon/types'
import { supabaseClient } from '@/lib/supabase'

export type Orderable = { id: string; sort_order: number }

type OrderableTable =
  'subjects' | 'content_items' | 'plans' | 'streams' | 'programs' | 'cities' | 'faq_items'

export async function persistOrder(table: OrderableTable, rows: Orderable[]): Promise<void> {
  // Every row in `table` already exists, so the upsert only ever touches
  // `id`/`sort_order` — but the generated `Insert` type (shared across every
  // `OrderableTable`) still demands each table's required columns, which a
  // reorder payload never carries. The assertion documents that gap instead
  // of widening the client itself.
  const payload = rows.map(row => ({ id: row.id, sort_order: row.sort_order })) as unknown as Array<
    Database['public']['Tables'][OrderableTable]['Insert']
  >
  const { error } = await supabaseClient.from(table).upsert(payload, { onConflict: 'id' })
  if (error) throw error
}
